// src/errors.ts
var ChatError = class extends Error {
  httpStatus;
  code;
  constructor(httpStatus, code, message) {
    super(`chat-server: ${code} (HTTP ${httpStatus}): ${message}`);
    this.name = "ChatError";
    this.httpStatus = httpStatus;
    this.code = code;
  }
};
function isAdminAuthFailed(err) {
  return err instanceof ChatError && err.code === "admin_auth_failed";
}
function isInvalidToken(err) {
  return err instanceof ChatError && (err.code === "invalid_token" || err.code === "missing_token");
}
function isTenantMismatch(err) {
  return err instanceof ChatError && err.code === "tenant_mismatch";
}
function isNoWritePermission(err) {
  return err instanceof ChatError && err.code === "no_write_permission";
}
function isBadRequest(err) {
  return err instanceof ChatError && (err.code === "bad_json" || err.code === "missing_uuid" || err.code === "invalid_tenant");
}
function isNotImplemented(err) {
  return err instanceof ChatError && err.code === "not_implemented";
}
async function parseErrorResponse(res) {
  let code = "transport_error";
  let message = res.statusText;
  try {
    const body = await res.json();
    if (body?.error?.code) code = body.error.code;
    if (body?.error?.message) message = body.error.message;
  } catch {
  }
  return new ChatError(res.status, code, message);
}

// src/transport/rest.ts
function toHttpBase(endpoint) {
  return endpoint.replace(/^wss:\/\//i, "https://").replace(/^ws:\/\//i, "http://").replace(/\/+$/, "");
}
async function publish(opts, params) {
  const base = toHttpBase(opts.endpoint);
  const url = `${base}/v3/publish/${encodeURIComponent(opts.tenant)}/${encodeURIComponent(params.channel)}`;
  const body = { message: params.message };
  if (params.meta !== void 0) body.meta = params.meta;
  if (params.storeInHistory !== void 0) body.storeInHistory = params.storeInHistory;
  const res = await opts.fetchImpl(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${opts.token}`
    },
    body: JSON.stringify(body)
  });
  if (!res.ok) throw await parseErrorResponse(res);
  const json = await res.json();
  return { timetoken: json.timetoken };
}

// src/transport/files.ts
async function sendFile(opts, params) {
  if (!params.channel) throw new Error("sendFile: channel is required");
  if (!params.file?.name || params.file.data === void 0) {
    throw new Error("sendFile: file.name and file.data are required");
  }
  const contentType = pickContentType(params.file);
  const base = toHttpBase(opts.endpoint);
  const reserve = await opts.fetchImpl(
    `${base}/v3/files/${encodeURIComponent(opts.tenant)}/${encodeURIComponent(params.channel)}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${opts.token}`
      },
      body: JSON.stringify({ name: params.file.name, contentType })
    }
  );
  if (!reserve.ok) throw await parseErrorResponse(reserve);
  const env = await reserve.json();
  const put = await opts.fetchImpl(env.uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": contentType },
    body: toFetchBody(params.file.data)
  });
  if (!put.ok) {
    const text = await put.text().catch(() => "");
    throw new ChatError(put.status, "s3_upload_failed", text || put.statusText);
  }
  const publishType = params.publishType ?? "file";
  const envelope = {
    type: publishType,
    file: { id: env.id, name: env.name, size: byteLength(params.file.data) }
  };
  if (params.uuid != null) {
    envelope.uuid = params.uuid;
  }
  if (params.message != null) {
    envelope.message = params.message;
  }
  const pubResult = await publish(opts, {
    channel: params.channel,
    message: envelope,
    storeInHistory: params.storeInHistory,
    meta: params.meta
  });
  return { id: env.id, name: env.name, timetoken: pubResult.timetoken };
}
function getFileUrl(opts, params) {
  const base = toHttpBase(opts.endpoint);
  return `${base}/v3/files/${encodeURIComponent(opts.tenant)}/${encodeURIComponent(params.channel)}/${encodeURIComponent(params.id)}/${encodeURIComponent(params.name)}?token=${encodeURIComponent(opts.token)}`;
}
async function downloadFile(opts, params) {
  const url = getFileUrl(opts, params);
  const res = await opts.fetchImpl(url, { method: "GET" });
  if (!res.ok) throw await parseErrorResponse(res);
  return new Uint8Array(await res.arrayBuffer());
}
function byteLength(data) {
  if (typeof data === "string") return new TextEncoder().encode(data).length;
  if (data instanceof ArrayBuffer) return data.byteLength;
  if (ArrayBuffer.isView(data)) return data.byteLength;
  if (typeof Blob !== "undefined" && data instanceof Blob) return data.size;
  return 0;
}
function pickContentType(file) {
  if (file.mimeType) return file.mimeType;
  const t = file.data?.type;
  if (typeof t === "string" && t.length > 0) return t;
  return "application/octet-stream";
}
function toFetchBody(data) {
  if (typeof data === "string") return data;
  if (data instanceof ArrayBuffer) return data;
  return data;
}

// src/transport/queries.ts
async function fetchMessages(opts, params) {
  if (!params.channels || params.channels.length === 0) {
    throw new Error("fetchMessages: channels must be non-empty");
  }
  const base = toHttpBase(opts.endpoint);
  const headers = { Authorization: `Bearer ${opts.token}`, Accept: "application/json" };
  const perChannel = await Promise.all(
    params.channels.map(async (ch) => {
      const url = new URL(`${base}/v3/history/${encodeURIComponent(opts.tenant)}/${encodeURIComponent(ch)}`);
      if (params.start !== void 0) url.searchParams.set("start", params.start);
      if (params.end !== void 0) url.searchParams.set("end", params.end);
      if (params.count !== void 0) url.searchParams.set("count", String(params.count));
      if (params.reverse) url.searchParams.set("reverse", "true");
      const res = await opts.fetchImpl(url.toString(), { method: "GET", headers });
      if (!res.ok) throw await parseErrorResponse(res);
      return { channel: ch, body: await res.json() };
    })
  );
  const out = { channels: {} };
  for (const { channel, body } of perChannel) {
    out.channels[channel] = body.channels[channel] ?? [];
    if (!out.more && body.more) out.more = body.more;
  }
  return out;
}
async function hereNow(opts, params) {
  if (!params.channels || params.channels.length === 0) {
    throw new Error("hereNow: channels must be non-empty");
  }
  const base = toHttpBase(opts.endpoint);
  const includeUUIDs = params.includeUUIDs !== false;
  const headers = { Authorization: `Bearer ${opts.token}`, Accept: "application/json" };
  const perChannel = await Promise.all(
    params.channels.map(async (ch) => {
      const url = new URL(
        `${base}/v3/presence/${encodeURIComponent(opts.tenant)}/${encodeURIComponent(ch)}/hereNow`
      );
      if (!includeUUIDs) url.searchParams.set("includeUUIDs", "false");
      const res = await opts.fetchImpl(url.toString(), { method: "GET", headers });
      if (!res.ok) throw await parseErrorResponse(res);
      return { channel: ch, body: await res.json() };
    })
  );
  const channels = {};
  let totalOccupancy = 0;
  for (const { channel, body } of perChannel) {
    const occupants = includeUUIDs ? (body.uuids ?? []).map((uuid) => ({ uuid })) : [];
    channels[channel] = { name: channel, occupancy: body.occupancy, occupants };
    totalOccupancy += body.occupancy;
  }
  return {
    totalChannels: Object.keys(channels).length,
    totalOccupancy,
    channels
  };
}

// src/transport/ws.ts
var WS_READY_OPEN = 1;
function toWsUrl(endpoint) {
  return endpoint.replace(/^https:\/\//i, "wss://").replace(/^http:\/\//i, "ws://").replace(/\/+$/, "");
}
var WsConnection = class {
  cfg;
  socket = null;
  hbTimer = null;
  reconnectTimer = null;
  reconnectAttempt = 0;
  state = { channels: /* @__PURE__ */ new Set(), withPresence: false };
  closed = false;
  /** True after a fatal close code (4401/4403): don't auto-reconnect. */
  fatal = false;
  constructor(cfg) {
    this.cfg = {
      setTimeoutImpl: (cb, ms) => setTimeout(cb, ms),
      clearTimeoutImpl: (h) => clearTimeout(h),
      randomImpl: Math.random,
      ...cfg
    };
  }
  connect() {
    if (this.closed || this.socket) return;
    const base = toWsUrl(this.cfg.endpoint);
    const url = `${base}/v3/subscribe?token=${encodeURIComponent(this.cfg.token)}&tenant=${encodeURIComponent(this.cfg.tenant)}`;
    const ws = new this.cfg.webSocketImpl(url);
    this.socket = ws;
    ws.onopen = () => {
      this.reconnectAttempt = 0;
      this.startHeartbeat();
      if (this.state.channels.size > 0) {
        this.send({
          op: "subscribe",
          channels: Array.from(this.state.channels),
          withPresence: this.state.withPresence,
          ...this.state.timetoken ? { timetoken: this.state.timetoken } : {}
        });
      }
    };
    ws.onmessage = (e) => {
      if (typeof e.data !== "string") return;
      let frame;
      try {
        frame = JSON.parse(e.data);
      } catch {
        return;
      }
      if (frame && typeof frame === "object" && "event" in frame) {
        this.cfg.dispatch(frame);
      }
    };
    ws.onclose = (e) => {
      this.socket = null;
      this.stopHeartbeat();
      const code = e.code;
      const status = {
        event: "status",
        category: this.categoryForCloseCode(code),
        statusCode: code,
        error: e.reason || void 0
      };
      this.cfg.dispatch(status);
      if (this.closed) return;
      if (code === 4401 || code === 4403) {
        this.fatal = true;
        return;
      }
      this.scheduleReconnect();
    };
    ws.onerror = () => {
    };
  }
  /** True if we have an OPEN socket. */
  isOpen() {
    return this.socket !== null && this.socket.readyState === WS_READY_OPEN;
  }
  /** Add channels to the subscription set; resend if connected. */
  subscribe(channels, withPresence, timetoken) {
    for (const ch of channels) this.state.channels.add(ch);
    if (withPresence) this.state.withPresence = true;
    if (timetoken !== void 0) this.state.timetoken = timetoken;
    if (this.isOpen()) {
      const op = {
        op: "subscribe",
        channels,
        withPresence: this.state.withPresence
      };
      if (timetoken !== void 0) op.timetoken = timetoken;
      this.send(op);
    } else if (!this.fatal) {
      this.connect();
    }
  }
  /** Remove channels from the subscription set; send unsubscribe if connected. */
  unsubscribe(channels) {
    for (const ch of channels) this.state.channels.delete(ch);
    if (channels.length === 0) {
      this.state.channels.clear();
    }
    if (this.isOpen()) {
      this.send({ op: "unsubscribe", channels });
    }
  }
  /** Force-close. Stops timers, no auto-reconnect. */
  close() {
    this.closed = true;
    this.stopHeartbeat();
    this.cancelReconnect();
    if (this.socket) {
      try {
        this.socket.close(1e3, "client closed");
      } catch {
      }
      this.socket = null;
    }
  }
  send(payload) {
    if (!this.socket) return;
    try {
      this.socket.send(JSON.stringify(payload));
    } catch {
    }
  }
  startHeartbeat() {
    this.stopHeartbeat();
    if (this.cfg.heartbeatIntervalMs <= 0) return;
    const tick = () => {
      if (this.isOpen()) this.send({ op: "hb" });
      this.hbTimer = this.cfg.setTimeoutImpl(tick, this.cfg.heartbeatIntervalMs);
    };
    this.hbTimer = this.cfg.setTimeoutImpl(tick, this.cfg.heartbeatIntervalMs);
  }
  stopHeartbeat() {
    if (this.hbTimer !== null) {
      this.cfg.clearTimeoutImpl(this.hbTimer);
      this.hbTimer = null;
    }
  }
  scheduleReconnect() {
    this.cancelReconnect();
    const attempt = this.reconnectAttempt++;
    const base = Math.min(
      this.cfg.maxReconnectDelayMs,
      this.cfg.initialReconnectDelayMs * 2 ** attempt
    );
    const jittered = Math.floor(base * (0.8 + 0.4 * this.cfg.randomImpl()));
    this.reconnectTimer = this.cfg.setTimeoutImpl(() => {
      this.reconnectTimer = null;
      if (!this.closed) this.connect();
    }, jittered);
  }
  cancelReconnect() {
    if (this.reconnectTimer !== null) {
      this.cfg.clearTimeoutImpl(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
  categoryForCloseCode(code) {
    if (code === 1e3) return "PNDisconnectedCategory";
    if (code === 4401 || code === 4403) return "PNAccessDeniedCategory";
    if (code === 4408) return "PNNetworkIssuesCategory";
    if (code === 4429) return "PNUnknownCategory";
    return "PNUnknownCategory";
  }
};

// src/client.ts
var DEFAULTS = {
  heartbeatIntervalMs: 3e4,
  initialReconnectDelayMs: 1e3,
  maxReconnectDelayMs: 3e4
};
var ChatPubNub = class {
  cfg;
  listeners = /* @__PURE__ */ new Set();
  ws;
  constructor(cfg) {
    if (!cfg.endpoint) throw new Error("ChatPubNub: endpoint is required");
    if (!cfg.tenant) throw new Error("ChatPubNub: tenant is required");
    if (!cfg.uuid) throw new Error("ChatPubNub: uuid is required");
    if (!cfg.token) throw new Error("ChatPubNub: token is required");
    const wsImpl = cfg.webSocketImpl ?? globalThis.WebSocket;
    if (!wsImpl) {
      throw new Error(
        "ChatPubNub: no WebSocket implementation. Pass `webSocketImpl` (e.g. from the `ws` package) on Node <22."
      );
    }
    const fetchImpl = cfg.fetchImpl ?? globalThis.fetch.bind(globalThis);
    if (!fetchImpl) {
      throw new Error("ChatPubNub: no fetch implementation. Pass `fetchImpl` on environments without global fetch.");
    }
    this.cfg = {
      ...DEFAULTS,
      ...cfg,
      webSocketImpl: wsImpl,
      fetchImpl
    };
    this.ws = new WsConnection({
      endpoint: this.cfg.endpoint,
      tenant: this.cfg.tenant,
      token: this.cfg.token,
      heartbeatIntervalMs: this.cfg.heartbeatIntervalMs,
      initialReconnectDelayMs: this.cfg.initialReconnectDelayMs,
      maxReconnectDelayMs: this.cfg.maxReconnectDelayMs,
      webSocketImpl: this.cfg.webSocketImpl,
      dispatch: (e) => this.dispatch(e)
    });
  }
  /**
   * Register a listener bundle. Returns a disposer that removes it.
   *
   *   const off = pubnub.addListener({ message: e => ..., status: e => ... });
   *   off();
   */
  addListener(listener) {
    this.listeners.add(listener);
    this.ws.connect();
    return () => this.removeListener(listener);
  }
  /** Drop a listener bundle previously passed to addListener. */
  removeListener(listener) {
    this.listeners.delete(listener);
  }
  /** Subscribe to one or more channels. */
  subscribe(params) {
    if (!params.channels || params.channels.length === 0) {
      throw new Error("ChatPubNub.subscribe: channels must be non-empty");
    }
    this.ws.subscribe(params.channels, params.withPresence === true, params.timetoken);
  }
  /** Unsubscribe from channels. Empty list = leave all. */
  unsubscribe(params) {
    this.ws.unsubscribe(params.channels ?? []);
  }
  /** Unsubscribe from every channel (alias for `unsubscribe({channels:[]})`). */
  unsubscribeAll() {
    this.ws.unsubscribe([]);
  }
  /**
   * Publish a message to a channel. Returns the assigned 17-digit timetoken.
   *
   * @throws ChatError on chat-server errors (use isInvalidToken / isNoWritePermission / etc).
   */
  async publish(params) {
    if (!params.channel) throw new Error("ChatPubNub.publish: channel is required");
    if (params.message === void 0 || params.message === null) {
      throw new Error("ChatPubNub.publish: message is required");
    }
    return publish(this.restOpts(), params);
  }
  restOpts() {
    return {
      endpoint: this.cfg.endpoint,
      tenant: this.cfg.tenant,
      token: this.cfg.token,
      fetchImpl: this.cfg.fetchImpl
    };
  }
  /**
   * Fetch persisted message history for one or more channels. PubNub
   * fetchMessages parity. Server caps `count` at 100 per channel; default 25.
   *
   * @throws ChatError (use isInvalidToken / isTenantMismatch).
   */
  fetchMessages(params) {
    return fetchMessages(this.restOpts(), params);
  }
  /**
   * Get the current subscribers on one or more channels (PubNub hereNow
   * parity). Requires `g:1` on each channel.
   *
   * @throws ChatError (use isInvalidToken / isTenantMismatch).
   */
  hereNow(params) {
    return hereNow(this.restOpts(), params);
  }
  /**
   * Upload a file and publish a file message in one call (PubNub sendFile
   * parity). Three steps internally: presign → S3 PUT → publish.
   *
   * @throws ChatError on any step (presign, S3 upload, or publish).
   */
  sendFile(params) {
    return sendFile(this.restOpts(), params);
  }
  /**
   * Return a download URL for a previously uploaded file. Synchronous (matches
   * PubNub) — the URL points at chat-server's GET endpoint with `?token=`,
   * which 302s to S3 when the browser dereferences it.
   *
   * Safe to drop directly into `<img src>` / `<a href>` / `<video src>`.
   */
  getFileUrl(params) {
    return getFileUrl(this.restOpts(), params);
  }
  /**
   * Download file bytes (follows chat-server 302 redirect to S3).
   *
   * @throws ChatError on non-2xx responses from chat-server.
   */
  downloadFile(params) {
    return downloadFile(this.restOpts(), params);
  }
  /** Tear down the WebSocket and stop reconnecting. The client is unusable after this. */
  disconnect() {
    this.ws.close();
    this.listeners.clear();
  }
  dispatch(e) {
    for (const l of this.listeners) {
      try {
        if (e.event === "message") l.message?.(e);
        else if (e.event === "message_edited") l.messageEdited?.(e);
        else if (e.event === "message_deleted") l.messageDeleted?.(e);
        else if (e.event === "presence") l.presence?.(e);
        else if (e.event === "status") l.status?.(e);
      } catch {
      }
    }
  }
};
export {
  ChatError,
  ChatPubNub,
  isAdminAuthFailed,
  isBadRequest,
  isInvalidToken,
  isNoWritePermission,
  isNotImplemented,
  isTenantMismatch
};
